import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  UnstyledButton,
  Text,
  ActionIcon,
  Tooltip,
  Button,
  Divider,
  Anchor,
  Group,
  Box,
  Avatar,
} from '@mantine/core';
import { IconPlus, IconChevronRight, IconShieldLock, IconFileText } from '@tabler/icons-react';
import { useAuth } from '../../context/AuthContext';
import { productsService } from '../../services/products';
import { usersService } from '../../services/users';
import { CATEGORY_EMOJI } from '../../utils/categories';

interface NavItemProps {
  emoji: string;
  avatarUrl?: string | null;
  label: string;
  to: string;
  active?: boolean;
  collapsed?: boolean;
  requiresAuth?: boolean;
}

function NavItem({ emoji, avatarUrl, label, to, active, collapsed, requiresAuth }: NavItemProps) {
  const { isAuthenticated } = useAuth();

  if (requiresAuth && !isAuthenticated) return null;

  const button = (
    <UnstyledButton
      component={Link}
      to={to}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.625rem',
        padding: collapsed ? '0.5rem' : '0.5rem 0.75rem',
        borderRadius: '0.625rem',
        width: '100%',
        justifyContent: collapsed ? 'center' : 'flex-start',
        backgroundColor: active ? 'var(--mantine-color-default-hover)' : 'transparent',
        transition: 'background-color 150ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--mantine-color-default-hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = active
          ? 'var(--mantine-color-default-hover)'
          : 'transparent';
      }}
    >
      {avatarUrl ? (
        <Avatar src={avatarUrl} size={26} radius="xl" style={{ flexShrink: 0 }} />
      ) : (
        <span style={{ fontSize: 20, lineHeight: 1, width: 26, textAlign: 'center', flexShrink: 0 }}>
          {emoji}
        </span>
      )}
      {!collapsed && (
        <Text size="sm" fw={active ? 700 : 500}>
          {label}
        </Text>
      )}
    </UnstyledButton>
  );

  if (collapsed) {
    return <Tooltip label={label} position="right" withArrow>{button}</Tooltip>;
  }

  return button;
}

interface LeftNavProps {
  collapsed?: boolean;
}

export function LeftNav({ collapsed = false }: LeftNavProps) {
  const location = useLocation();
  const { isAuthenticated, user, isModerator } = useAuth();

  const { data: topCategoriesData = [] } = useQuery({
    queryKey: ['categories-top'],
    queryFn: () => productsService.getTopCategories(8),
    staleTime: 300_000,
  });
  const topCategories = topCategoriesData;

  const { data: recentReviews = [] } = useQuery({
    queryKey: ['recent-reviews-nav'],
    queryFn: () => usersService.getRecentlyViewed(5),
    enabled: isAuthenticated && !collapsed,
    staleTime: 60_000,
  });

  const [recentTags, setRecentTags] = useState<{ slug: string; name: string }[]>([]);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('cartcritic_recent_tags') ?? '[]');
      setRecentTags(Array.isArray(stored) ? stored : []);
    } catch {
      setRecentTags([]);
    }
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <Stack gap={2} h="100%" justify="space-between">
      <Stack gap={2}>

        {/* ── Primary Navigation ── */}
        <NavItem emoji="🏠" label="Home" to="/" active={isActive('/')} collapsed={collapsed} />
        <NavItem emoji="🔍" label="Discover" to="/discover" active={isActive('/discover')} collapsed={collapsed} />
        <NavItem emoji="🪪" avatarUrl={user?.profileImageUrl} label="Profile" to={user ? `/users/${user.username}` : '/login'} active={location.pathname === `/users/${user?.username}`} collapsed={collapsed} requiresAuth />
        <NavItem emoji="🔖" label="Saved" to="/saved" active={isActive('/saved')} collapsed={collapsed} requiresAuth />
        <NavItem emoji="🤝" label="Following" to="/following" active={isActive('/following')} collapsed={collapsed} requiresAuth />
        <NavItem emoji="⚙️" label="Settings" to="/settings" active={isActive('/settings')} collapsed={collapsed} requiresAuth />
        {isModerator && (
          <NavItem emoji="🛡️" label="Admin" to="/admin" active={isActive('/admin')} collapsed={collapsed} />
        )}

        {/* Post Review button */}
        {isAuthenticated && (
          <>
            {collapsed ? (
              <Tooltip label="New Review" position="right" withArrow>
                <ActionIcon
                  component={Link}
                  to="/reviews/create"
                  size="xl"
                  radius="xl"
                  variant="filled"
                  color="brand"
                  style={{ marginTop: '0.5rem' }}
                >
                  <IconPlus size={22} />
                </ActionIcon>
              </Tooltip>
            ) : (
              <Button
                component={Link}
                to="/reviews/create"
                size="md"
                radius="xl"
                fullWidth
                style={{ marginTop: '0.5rem' }}
              >
                Post Review
              </Button>
            )}
          </>
        )}

        {/* ── Browse Categories ── */}
        {!collapsed && (
          <>
            <Divider my="sm" />
            <Text size="sm" fw={600} px="xs" mb={4}>
              Browse
            </Text>
            {topCategories.map(({ category, emoji }) => (
              <UnstyledButton
                key={category}
                component={Link}
                to={`/categories/${encodeURIComponent(category)}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.625rem',
                  padding: '0.375rem 0.75rem',
                  borderRadius: '0.625rem',
                  transition: 'background-color 150ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--mantine-color-default-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span style={{ fontSize: 20, lineHeight: 1, width: 26, textAlign: 'center', flexShrink: 0 }}>
                  {emoji ?? CATEGORY_EMOJI[category] ?? '📦'}
                </span>
                <Text size="sm" lineClamp={1}>{category}</Text>
              </UnstyledButton>
            ))}
            <Box px="xs" mt={4}>
              <Anchor component={Link} to="/categories" size="xs" c="dimmed">
                <Group gap={4}>
                  <span>See all categories</span>
                  <IconChevronRight size={12} />
                </Group>
              </Anchor>
            </Box>

            {/* Recent Hashtags */}
            {recentTags.length > 0 && (
              <>
                <Divider my="sm" />
                <Text size="sm" fw={600} px="xs" mb={4}>
                  Recent Tags
                </Text>
                <Stack gap="xs" px="xs">
                  {recentTags.map(tag => (
                    <Anchor
                      key={tag.slug}
                      component={Link}
                      to={`/tags/${tag.slug}`}
                      size="sm"
                      fw={500}
                      style={{ textDecoration: 'none' }}
                    >
                      #{tag.name}
                    </Anchor>
                  ))}
                </Stack>
              </>
            )}

            {/* Recent Reviews */}
            {recentReviews.length > 0 && (
              <>
                <Divider my="sm" />
                <Text size="sm" fw={600} px="xs" mb={4}>
                  Recent Reviews
                </Text>
                <Stack gap="xs" px="xs">
                  {recentReviews.map(review => (
                    <Anchor
                      key={review.id}
                      component={Link}
                      to={`/reviews/${review.id}`}
                      size="sm"
                      fw={500}
                      style={{ textDecoration: 'none' }}
                      lineClamp={1}
                    >
                      {review.productName} · @{review.username}
                    </Anchor>
                  ))}
                </Stack>
              </>
            )}

            {/* Footer links */}
            <Divider my="sm" />
            {[
              { icon: <IconShieldLock size={18} />, label: 'Privacy Policy', to: '/privacy' },
              { icon: <IconFileText size={18} />, label: 'User Agreement', to: '/terms' },
            ].map(({ icon, label, to }) => (
              <UnstyledButton
                key={to}
                component={Link}
                to={to}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.625rem',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.625rem',
                  width: '100%',
                  color: 'var(--mantine-color-dimmed)',
                  transition: 'background-color 150ms ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mantine-color-default-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span style={{ width: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {icon}
                </span>
                <Text size="sm" c="dimmed">{label}</Text>
              </UnstyledButton>
            ))}
            <Text size="xs" c="dimmed" px="xs" mt={4}>
              CartCritic © {new Date().getFullYear()}
            </Text>
            <Text size="xs" c="dimmed" px="xs" mt={2}>
              As an Amazon Associate I earn from qualifying purchases.
            </Text>
          </>
        )}

      </Stack>
    </Stack>
  );
}
